import { prodFixOn } from '../../../utils/prod-fix-flags';
import {
    cardInputFingerprints,
    cardUploadIds,
    findUnchanged,
    PendingRun,
    PriorRunContext,
    SourceMatch,
} from '../../../utils/rerun-canonical';

/**
 * FIX-8 (UX-SPEC.md sections 8.3 and 8.4): what the tool page and the Re-edit modal say about a
 * run that repeats one the user already started. Pure over its inputs (the would-be requests, the
 * cards this browser holds, the clock), so the surfaces only render the answer.
 *
 * Feature flag `rerunDedupeOffer` off = every function here answers "nothing to say", which is
 * today's page.
 */

/** The flag, read the way every kill switch on this page is: only an exact `true` turns it on. */
export function rerunDedupeOfferEnabled(flags: { rerunDedupeOffer?: unknown } | null | undefined): boolean {
    // T1's one reader of the prod-fix flags (utils/prod-fix-flags.ts); same exact-`true` rule.
    return prodFixOn(flags as any, 'rerunDedupeOffer');
}

export interface RerunInputs {
    /** `featureFlags.rerunDedupeOffer === true`. */
    enabled: boolean;
    /** What the schedule call would post, one entry per card (`FileProcessEditorComponent.describeScheduleRequests`). */
    pending: () => PendingRun[];
    /** The cards this browser holds (`FileManagerService.getFileUploads()`). */
    cards: any[] | null | undefined;
    ctx: PriorRunContext;
    /** The options context's own default summary, so absent equals default. */
    defaults?: Record<string, unknown> | null;
}

export type ToolPageRerunView =
    | { kind: 'none' }
    | { kind: 'running' }
    | { kind: 'succeeded'; priors: any[]; endedAt: number };

const NONE: ToolPageRerunView = { kind: 'none' };

function endOf(card: any): number {
    const t = new Date(card?.processEndTime ?? card?.result?.end ?? NaN).getTime();
    return Number.isFinite(t) ? t : 0;
}

/**
 * Cheap gate before the real comparison: building the would-be requests clones options per file,
 * and the card list changes on every progress tick. Only when every staged source is already on
 * some card is the full check worth running.
 */
function everySourceKnown(keys: (string | null)[], cards: any[], by: SourceMatch): boolean {
    if (keys.length === 0 || keys.some(k => k === null)) return false;
    const known = new Set<string>();
    cards.forEach(card => {
        (by === 'uploadIds' ? cardUploadIds(card) : cardInputFingerprints(card)).forEach(k => { if (k) known.add(k); });
    });
    return keys.every(k => known.has(k as string));
}

/**
 * The tool page, fresh upload (section 8.1 item 1: name + size + lastModified). `stagedFingerprints`
 * is read straight off the staged files, for the cheap gate.
 */
export function toolPageRerunView(inputs: RerunInputs, stagedFingerprints: (string | null)[]): ToolPageRerunView {
    if (!inputs.enabled || !inputs.cards || inputs.cards.length === 0) return NONE;
    if (!everySourceKnown(stagedFingerprints, inputs.cards, 'fingerprint')) return NONE;
    const match = findUnchanged(inputs.pending(), inputs.cards, 'fingerprint', inputs.ctx, inputs.defaults);
    if (!match) return NONE;
    if (match.state === 'running') return { kind: 'running' };
    return { kind: 'succeeded', priors: match.priors, endedAt: Math.max(...match.priors.map(endOf)) };
}

export type ReeditRerunState = { running: boolean; unchangedSucceeded: boolean; prior: any | null };

const NOT_UNCHANGED: ReeditRerunState = { running: false, unchangedSucceeded: false, prior: null };

/**
 * The Re-edit modal (section 8.1 item 1: the same upload ids in the same order). The two booleans
 * feed `resolveReeditFooterMode`; `prior` is the card whose result "Download result" fetches.
 * `canDownloadExisting` is false when the modal was not given the card's own Download path -- then
 * there is no honest "Download result" to offer and the footer stays as it is today. `opened` names the
 * card the modal was opened from: a match on any OTHER card is offered only when `opened.canDownload` says
 * that card's result can be fetched directly.
 */
export function reeditRerunState(
    inputs: RerunInputs,
    canDownloadExisting: boolean,
    opened?: ReeditOpenedFrom,
): ReeditRerunState {
    if (!inputs.enabled || !inputs.cards || inputs.cards.length === 0) return NOT_UNCHANGED;
    const pending = inputs.pending();
    if (pending.length === 0) return NOT_UNCHANGED;
    if (!everySourceKnown(pending.flatMap(p => p.uploadIds), inputs.cards, 'uploadIds')) return NOT_UNCHANGED;
    const match = findUnchanged(pending, inputs.cards, 'uploadIds', inputs.ctx, inputs.defaults);
    if (!match) return NOT_UNCHANGED;
    if (match.state === 'running') return { running: true, unchangedSucceeded: false, prior: match.priors[0] };
    if (!canDownloadExisting || match.priors.length !== 1) return NOT_UNCHANGED;
    const prior = match.priors[0];
    // fu-t10 F1: the result on offer is the NEWEST identical run's, which is not always the card the modal was
    // opened from -- Re-edit on a failed (or older) card whose unchanged re-run since succeeded matches that newer
    // card. The opener's Download key would then fetch the wrong card's output (or nothing, on a failed card), so
    // another card is offered only when its own one-file Download path can fetch it; otherwise today's footer.
    if (opened && prior?.id !== opened.cardId && !(opened.canDownload?.(prior) ?? false)) return NOT_UNCHANGED;
    return { running: false, unchangedSucceeded: true, prior };
}

/** The card a Re-edit modal was opened from, and how to tell whether ANOTHER card's result can be downloaded. */
export interface ReeditOpenedFrom {
    cardId: string | null | undefined;
    canDownload?: (card: any) => boolean;
}

/**
 * "Shown at most once per file per page visit" (section 8.4). One ledger per tool-page instance;
 * a visit is the component's life. The offer is due when ANY staged file has not been asked about
 * yet, so a new file in an otherwise-asked batch is asked about once too.
 */
export class RerunOfferLedger {
    private readonly asked = new Set<string>();

    isDue(stagedFingerprints: (string | null)[]): boolean {
        return stagedFingerprints.length > 0 && stagedFingerprints.some(k => k !== null && !this.asked.has(k));
    }

    markAsked(stagedFingerprints: (string | null)[]): void {
        stagedFingerprints.forEach(k => { if (k) this.asked.add(k); });
    }
}
